/**
 * Display-mode detection for installed Home Screen / standalone web apps.
 */

type IosNavigator = Navigator & { standalone?: boolean };

/** True when running as an iOS / iPadOS Home Screen app (`navigator.standalone`). */
export function isIosHomeScreenApp(): boolean {
  if (typeof window === 'undefined') return false;
  return (window.navigator as IosNavigator).standalone === true;
}

/**
 * True when the app runs as any installed app: an iOS Home Screen app, or a
 * browser reporting the manifest `standalone` / `fullscreen` display mode.
 */
export function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return false;
  if (isIosHomeScreenApp()) return true;
  if (typeof window.matchMedia !== 'function') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches
  );
}

/**
 * Popup windows opened from an iOS Home Screen app open in a separate browser
 * sheet that cannot report back to the opener, so interactive sign-in uses a
 * full-page redirect there. Desktop and Android installed apps handle popups
 * normally and keep the popup flow. Evaluated once at module load: an iOS
 * Home Screen app cannot move into a browser tab while the page is open.
 */
export const iosHomeScreenApp: boolean = isIosHomeScreenApp();

/**
 * Whether interactive Microsoft sign-in may use a popup. Requires the saved
 * popup interaction mode and a registered bridge page; iOS Home Screen apps
 * always use the root redirect instead.
 */
export function interactiveSignInUsesPopup(
  interactionMode: string | undefined,
  hasBridge: boolean,
  inIosHomeScreenApp: boolean = iosHomeScreenApp
): boolean {
  return interactionMode === 'popup' && hasBridge && !inIosHomeScreenApp;
}
